import { CLOUDINARY_CLOUDNAME, CLOUDINARY_UPLOAD_PRESET_LARGE } from 'env'

export const MAX_VIDEO_SIZE_MB = 100
export const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/mov',
]

const UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUDNAME}/video/upload`

export const getVideoFileError = file => {
  if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
    return 'Upload an MP4, MOV, or WebM video.'
  }
  if (file.size > MAX_VIDEO_SIZE_MB * 1024 * 1024) {
    return `Videos must be ${MAX_VIDEO_SIZE_MB}MB or smaller.`
  }
  return null
}

/**
 * Uploads a video straight to Cloudinary and resolves with its delivery URL.
 * Pass an `AbortSignal` as `signal` to cancel an upload in flight; the promise
 * then rejects with an error whose `name` is `AbortError`.
 */
export const uploadVideo = (file, { folder, onProgress, signal } = {}) =>
  new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET_LARGE)
    if (folder) formData.append('folder', folder)

    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', event => {
      if (onProgress && event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const response = JSON.parse(xhr.responseText)
        resolve({
          url: response.secure_url,
          publicId: response.public_id,
          duration: response.duration,
        })
      } else {
        reject(new Error('Upload failed'))
      }
    })
    xhr.addEventListener('error', () => reject(new Error('Upload failed')))

    if (signal) {
      const abort = () => {
        xhr.abort?.()
        const error = new Error('Upload cancelled')
        error.name = 'AbortError'
        reject(error)
      }
      if (signal.aborted) {
        abort()
        return
      }
      signal.addEventListener('abort', abort, { once: true })
    }

    xhr.open('POST', UPLOAD_URL)
    xhr.send(formData)
  })
